import React, { useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import { pushEvent } from '../utils/analytics';

const NAV = [
  { to: '/our-beer', label: 'Our Beer', id: 'nav-our-beer' },
  { to: '/find-us', label: 'Find Us', id: 'nav-find-us' },
  { to: '/our-story', label: 'Our Story', id: 'nav-our-story' },
  { to: '/events', label: 'Events', id: 'nav-events' },
  { to: '/horeca', label: 'For Trade', id: 'nav-for-trade' },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const location = useLocation();
  const menuRef = useRef(null);
  const toggleBtnRef = useRef(null);
  const firstLinkRef = useRef(null);

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    // Return focus to the toggle button so keyboard users don't lose their place
    if (toggleBtnRef.current) toggleBtnRef.current.focus();
  }, []);

  // Close menu on route change
  useEffect(() => { setIsMenuOpen(false); }, [location.pathname]);

  // Lock scroll + trap keyboard focus while menu is open
  useEffect(() => {
    if (!isMenuOpen) {
      document.body.style.overflow = '';
      return undefined;
    }
    document.body.style.overflow = 'hidden';

    // Focus first item shortly after opening
    const t = setTimeout(() => {
      if (firstLinkRef.current) firstLinkRef.current.focus();
    }, 30);

    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu();
        return;
      }
      if (e.key === 'Tab' && menuRef.current) {
        const focusables = menuRef.current.querySelectorAll(
          'a, button, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKey);

    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [isMenuOpen, closeMenu]);

  return (
    <header className="nb-header" data-testid="site-header">
      <div className="nb-header-inner">
        <Link
          to="/"
          onClick={() => setIsMenuOpen(false)}
          className="nb-header-brand"
          data-testid="header-logo-btn"
          aria-label="No-Belly Bier home"
        >
          <img
            src="https://customer-assets-m6fa6gv7.emergentagent.net/job_044e7215-b684-4e8a-bc75-b8b1576b06e1/artifacts/tue77akt_Logo.webp"
            alt="No-Belly Bier"
            className="nb-header-logo"
          />
        </Link>

        <nav className="nb-header-nav" aria-label="Primary">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.id}
              aria-current={location.pathname === item.to ? 'page' : undefined}
              className={`nb-nav-link ${location.pathname === item.to ? 'active' : ''}`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <Link
          to="/find-us"
          className="nb-cta-primary nb-header-cta-desktop"
          data-testid="header-cta-find-nobelly"
          onClick={() => pushEvent('find_store_click', { button_location: 'header_desktop' })}
        >
          Find No-Belly
          <ArrowRight size={16} aria-hidden="true" />
        </Link>

        <button
          ref={toggleBtnRef}
          type="button"
          className="nb-mobile-toggle"
          onClick={() => setIsMenuOpen((v) => !v)}
          aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={isMenuOpen}
          aria-controls="nb-mobile-nav"
          data-testid="mobile-menu-toggle"
        >
          {isMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
        </button>
      </div>

      {isMenuOpen && (
        <nav
          id="nb-mobile-nav"
          ref={menuRef}
          className="nb-mobile-nav"
          aria-label="Mobile"
        >
          {NAV.map((item, i) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={closeMenu}
              ref={i === 0 ? firstLinkRef : undefined}
              className="nb-mobile-link"
              aria-current={location.pathname === item.to ? 'page' : undefined}
              data-testid={`mobile-${item.id}`}
            >
              {item.label}
            </Link>
          ))}
          <Link
            to="/find-us"
            onClick={() => {
              pushEvent('find_store_click', { button_location: 'header_mobile' });
              closeMenu();
            }}
            className="nb-cta-primary nb-mobile-cta"
            data-testid="mobile-cta-find-nobelly"
          >
            Find No-Belly
          </Link>
        </nav>
      )}
    </header>
  );
};

export default Header;
